import { useState, useEffect } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useChat } from '../../contexts/ChatContext';
import { formatDate } from '../../utils/chatTime';

/**
 * Search across the member's own conversations.
 *
 * The server joins on chat_participants, so results are already scoped — this
 * cannot surface a conversation the member is not in. It matches message text
 * and attachment filenames, not sender names.
 */
export default function MessageSearch({ onOpenResult }) {
  const { searchMessages, timezone } = useChat();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const term = query.trim();

    // The endpoint requires 2+ characters, and searching on every keystroke
    // would burn the 30/min rate limit almost immediately.
    if (term.length < 2) {
      setResults([]);
      setSearched(false);
      return undefined;
    }

    let cancelled = false;
    const handle = setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        const response = await searchMessages(term);
        if (!cancelled) {
          setResults(response.data || []);
          setSearched(true);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, searchMessages]);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="p-3 border-b border-neutral-300">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary/35" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search messages and file names…"
            autoFocus
            className="w-full pl-9 pr-3 py-2 rounded-[10px] border border-neutral-300 bg-white text-sm text-primary
                       placeholder:text-primary/35 focus:outline-none focus:ring-2
                       focus:ring-accent/40 focus:border-accent transition-all"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {error && <p className="px-4 py-3 text-sm text-alert">{error}</p>}

        {!error && query.trim().length < 2 && (
          <p className="px-4 py-6 text-sm text-primary/40 text-center">
            Search your conversations by message text or attached file name.
          </p>
        )}

        {!error && searching && (
          <p className="px-4 py-3 text-sm text-primary/40">Searching…</p>
        )}

        {!error && !searching && searched && results.length === 0 && (
          <div className="px-4 py-6 text-center">
            <p className="text-sm text-primary/40">No messages found.</p>
            <p className="text-xs text-primary/30 mt-1">
              Searches message text and attached file names, not people&apos;s names.
            </p>
          </div>
        )}

        <ul className="divide-y divide-neutral-300">
          {results.map((result) => (
            <li key={result.id}>
              <button
                onClick={() => onOpenResult(result.conversationId)}
                className="w-full text-left px-4 py-3 hover:bg-highlight/40 transition-colors"
              >
                <div className="flex items-baseline gap-2 mb-0.5">
                  <span className="text-xs font-medium text-primary truncate">
                    {result.conversationName || 'Conversation'}
                  </span>
                  <span className="ml-auto text-[11px] text-primary/40 shrink-0">
                    {formatDate(result.createdAt, timezone)}
                  </span>
                </div>
                {/* Rendered as text, never HTML — same rule as the message list. */}
                <p className="text-sm text-primary/75 line-clamp-2 whitespace-pre-wrap break-words">
                  {result.body}
                </p>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
